import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { staffService } from '../../services/apiService';

const StaffCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    emailPrefix: '',
    role: '',
    department: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });

  const roles = [
    { value: 'LIBRARY_STAFF', label: 'Library' },
    { value: 'HOD', label: 'Head of Department (HOD)' },
    { value: 'ACCOUNTS_SECTION', label: 'Accounts Section' },
    { value: 'EXAM_SECTION', label: 'Exam Section' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'FACULTY', label: 'Faculty' },
    { value: 'STAFF', label: 'Staff' }
  ];

  const departments = [
    { value: 'CS', label: 'CS' },
    { value: 'CD', label: 'CD' },
    { value: 'CI', label: 'CI' },
    { value: 'CY', label: 'CY' },
    { value: 'EC', label: 'EC' },
    { value: 'ME', label: 'ME' }
  ];

  const isDepartmentRequired = formData.role === 'HOD' || formData.role === 'FACULTY';

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.emailPrefix || !formData.role || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate department for HOD and FACULTY
    if ((formData.role === 'HOD' || formData.role === 'FACULTY') && !formData.department) {
      toast.error(`Department is required for ${formData.role === 'HOD' ? 'Head of Department' : 'Faculty'} role`);
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const collegeEmail = `${formData.emailPrefix.trim()}.nc@cambridge.edu.in`;
      
      await staffService.createStaff({
        name: formData.name,
        collegeEmail,
        role: formData.role,
        department: formData.department || null,
        phoneNumber: formData.phoneNumber || null,
        password: formData.password
      });

      toast.success('Staff member created successfully!');
      navigate('/admin/staff');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errors.forEach(err => toast.error(err.msg || err.message));
      } else {
        toast.error(error.response?.data?.error || 'Failed to create staff member');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextFormData = { ...formData, [name]: value };

    if (name === 'role') {
      const nextRequiresDept = value === 'HOD' || value === 'FACULTY';
      if (nextRequiresDept && !nextFormData.department) {
        nextFormData.department = departments[0]?.value || '';
      }
    }

    setFormData(nextFormData);
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/staff')}
            className="mb-4 text-slate-600 hover:text-slate-900 flex items-center gap-2"
          >
            ← Back to Staff Management
          </button>
          <h1 className="text-3xl font-bold text-slate-900">Create New Staff Member</h1>
          <p className="text-slate-600 mt-2">Add a new staff account with specific role and permissions</p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                College Email *
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="emailPrefix"
                  required
                  value={formData.emailPrefix}
                  onChange={handleChange}
                  placeholder="firstname.lastname"
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <span className="text-slate-600">.nc@cambridge.edu.in</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Example: john.doe will become john.doe.nc@cambridge.edu.in
              </p>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Role *
              </label>
              <select
                name="role"
                required
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Department {isDepartmentRequired && <span className="text-red-500">*</span>}
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleChange}
                required={isDepartmentRequired}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {!isDepartmentRequired && <option value="">GLOBAL (No Limit)</option>}
                {departments.map((dept) => (
                  <option key={dept.value} value={dept.value}>
                    {dept.label}
                  </option>
                ))}
              </select>
              {isDepartmentRequired && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ Department is required for {formData.role === 'HOD' ? 'Head of Department' : 'Faculty'} role
                </p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password (min 6 characters)"
                  className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Confirm Password *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter password"
                  className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Permissions Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Role Permissions:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {formData.role === 'LIBRARY_STAFF' && (
                  <li>• Can approve NOC requests (2nd in sequence)</li>
                )}
                {formData.role === 'HOD' && (
                  <li>• Can approve NOC requests (3rd in sequence)</li>
                )}
                {formData.role === 'ACCOUNTS_SECTION' && (
                  <li>• Can approve NOC requests (1st in sequence)</li>
                )}
                {formData.role === 'EXAM_SECTION' && (
                  <li>• Can approve NOC requests (4th/final in sequence)</li>
                )}
                {formData.role === 'ADMIN' && (
                  <li>• Full system access and permissions</li>
                )}
                {formData.role === 'FACULTY' && (
                  <li>• Can manage academic events and student records</li>
                )}
                {formData.role === 'STAFF' && (
                  <li>• Basic staff permissions</li>
                )}
                {!formData.role && (
                  <li className="text-slate-600">Select a role to see permissions</li>
                )}
              </ul>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/admin/staff')}
                disabled={loading}
                className="flex-1 py-3 px-6 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 px-6 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="spinner border-2 border-white border-t-transparent w-5 h-5"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Staff Member'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StaffCreate;
